// @owners { team: patients-team }
/**
 * Imperative NAD fetch for cart/checkout: React Query cache + {@link fetchNextAvailableDatesV3}, with Redux actions
 * so existing selectors keep working (NAD-1 bridge). Call sites migrate in NAD-2+.
 */
import { type QueryClient } from '@tanstack/react-query';
import {
  type FetchNextAvailableDatesOptions,
  fetchNextAvailableDatesFailed,
  fetchNextAvailableDatesSucceeded,
  fetchingNextAvailableDates,
  isNextAvailableDatesFetchSatisfiedByCache,
} from '~shared/actions/nextAvailableDates';
import { getScheduleablePrescriptions } from '~shared/features/my-meds/selectors/getScheduleablePrescriptions';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { staleTimes } from '~shared/queries/constants';
import {
  type FetchNextAvailableDatesV3Params,
  fetchNextAvailableDatesV3,
} from '~shared/queries/fetchNextAvailableDatesV3';
import { type ReduxDispatch, type ReduxStateShared } from '~shared/types';
import { type APIError } from '~shared/types/APIError';
import { type NextAvailableDateResponse } from '~shared/types/clients';

export type ResolveCheckoutNextAvailableDatesResult =
  | { kind: 'no_location' }
  | { kind: 'empty_success' }
  | { kind: 'fetch'; params: FetchNextAvailableDatesV3Params };

const noLocationError = (): APIError => ({
  details: {
    message: 'One of address or facility is required.',
    statusCode: 400,
    statusType: 'error',
  },
  message: 'One of address or facility is required.',
  name: 'error',
});

const toAPIError = (err: unknown): APIError => {
  if (err && typeof err === 'object' && 'message' in err) {
    return err as APIError;
  }
  return {
    message: typeof err === 'string' ? err : 'Unknown error',
    name: 'error',
    details: {
      message: typeof err === 'string' ? err : 'Unknown error',
      statusCode: 500,
      statusType: 'error',
    },
  };
};

/**
 * Resolves v3 NAD params from Redux the same way {@link fetchNextAvailableDates} does
 * (unbundled default from scheduleable rx, bundled from options, optional `shipmentID` from checkout state).
 */
export const resolveCheckoutNextAvailableDatesParams = (
  state: ReduxStateShared,
  options: FetchNextAvailableDatesOptions,
): ResolveCheckoutNextAvailableDatesResult => {
  const addressID = options.addressID;
  const facilityID = getNextAvailableDateFacilityID(state);
  const unbundled =
    options.unbundledPrescriptionIDs ||
    getScheduleablePrescriptions(state, { nextAvailableDateAddressID: addressID }).map((rx) => rx.id);
  const bundled = options.bundledPrescriptionIDs || [];
  const shipmentID = getShipmentID(state);

  if (!addressID && !facilityID) {
    return { kind: 'no_location' };
  }

  if (unbundled.length === 0 && bundled.length === 0 && !shipmentID) {
    return { kind: 'empty_success' };
  }

  const params: FetchNextAvailableDatesV3Params = {};

  if (typeof addressID === 'number') {
    params.addressID = addressID;
  } else if (typeof facilityID === 'number') {
    params.facilityID = facilityID;
  }

  params.unbundledPrescriptionIDs = unbundled;
  params.bundledPrescriptionIDs = bundled;

  if (typeof shipmentID === 'number') {
    params.shipmentID = shipmentID;
  }

  return { kind: 'fetch', params };
};

/** Query key for the resolved checkout NAD request (sorted ID lists, matches {@link nextAvailableDatesQueryKeys.forCheckout}). */
export const getCheckoutNextAvailableDatesQueryKey = (params: FetchNextAvailableDatesV3Params) =>
  nextAvailableDatesQueryKeys.forCheckout({
    addressID: params.addressID,
    facilityID: params.facilityID,
    unbundledPrescriptionIDs: params.unbundledPrescriptionIDs ?? [],
    bundledPrescriptionIDs: params.bundledPrescriptionIDs ?? [],
    shipmentID: params.shipmentID ?? null,
  });

/**
 * Fetches NAD via React Query and dispatches Redux bridge actions. Mirrors legacy thunk ordering:
 * no location → fail; Redux cache satisfied → succeeded([]); empty scope → succeeded([]); else LOADING + fetchQuery.
 */
export const fetchCheckoutNextAvailableDates = async (
  queryClient: QueryClient,
  dispatch: ReduxDispatch,
  getState: () => ReduxStateShared,
  options: FetchNextAvailableDatesOptions,
): Promise<boolean> => {
  const state = getState();
  const resolved = resolveCheckoutNextAvailableDatesParams(state, options);

  if (resolved.kind === 'no_location') {
    dispatch(fetchNextAvailableDatesFailed(noLocationError()));
    return false;
  }

  if (isNextAvailableDatesFetchSatisfiedByCache(state, options)) {
    dispatch(fetchNextAvailableDatesSucceeded([]));
    return true;
  }

  if (resolved.kind === 'empty_success') {
    dispatch(fetchNextAvailableDatesSucceeded([]));
    return true;
  }

  const { params } = resolved;
  const queryKey = getCheckoutNextAvailableDatesQueryKey(params);

  const staleTimeMs = staleTimes.fiveMinutes;
  const qState = queryClient.getQueryState(queryKey);
  const cachedData = queryClient.getQueryData<NextAvailableDateResponse[]>(queryKey);
  if (cachedData !== undefined && qState?.status === 'success' && Date.now() < qState.dataUpdatedAt + staleTimeMs) {
    dispatch(fetchNextAvailableDatesSucceeded(cachedData));
    return true;
  }

  dispatch(fetchingNextAvailableDates());

  try {
    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => fetchNextAvailableDatesV3(params),
      staleTime: staleTimeMs,
    });
    dispatch(fetchNextAvailableDatesSucceeded(data));
    return true;
  } catch (err) {
    dispatch(fetchNextAvailableDatesFailed(toAPIError(err)));
    return false;
  }
};
